import { useCallback, useEffect, useState } from 'react';
import { mapSettings, type MapKpiSetting } from '../api/mapSettings';
import { IconArrowDown, IconArrowUp, IconRefreshCw } from '../icons';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { Alert, Toggle } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';

/**
 * Harita Göstergeleri — which figures the map card shows, and in what order.
 *
 * <p>A regional director and a quality manager open the same map and want different numbers
 * on it, so this is the customer's choice rather than ours. The one thing worse than showing
 * too little is showing four figures nobody asked for, at which point people stop reading
 * the card at all.</p>
 */
export function MapSettings() {
  const t = useTranslate();

  const [kpis, setKpis] = useState<MapKpiSetting[]>([]);
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setKpis(await mapSettings.list());
      setError(null);
      setDirty(false);
    } catch (e) {
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const change = (next: MapKpiSetting[]) => {
    // Renumbered from their position, so the order sent is always 0..3 with no gaps —
    // moving one up and another down could otherwise leave two claiming the same place.
    setKpis(next.map((k, i) => ({ ...k, order: i })));
    setDirty(true);
    setSaved(false);
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= kpis.length) return;

    const next = [...kpis];
    const [taken] = next.splice(from, 1);
    next.splice(to, 0, taken!);
    change(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      setKpis(await mapSettings.save(kpis));
      setError(null);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const shown = kpis.filter((k) => k.isEnabled).length;

  return (
    <>
      <PageHeader
        crumbs={[t('app.definitions')]}
        title={t('mapSettings.title')}
        subtitle={t('mapSettings.subtitle')}
        action={
          <Button onClick={() => void save()} disabled={!dirty || saving || loading}>
            {t('common.save')}
          </Button>
        }
        aside={
          saved ? <Badge tone="comment">{t('mapSettings.saved')}</Badge> : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {shown === 0 && !loading ? (
        <Alert tone="warning">{t('mapSettings.noneShown')}</Alert>
      ) : null}

      <Card>
        {loading ? (
          <p className="nv-muted">
            <IconRefreshCw className="nv-spin" /> {t('common.loading')}
          </p>
        ) : (
          <ol className="nv-kpilist">
            {kpis.map((kpi, i) => (
              <li key={kpi.kpi}>
                <div className="nv-kpilist__order">
                  <IconButton
                    label={t('mapSettings.moveUp')}
                    icon={IconArrowUp}
                    onClick={() => move(i, i - 1)}
                    disabled={i === 0}
                  />
                  <IconButton
                    label={t('mapSettings.moveDown')}
                    icon={IconArrowDown}
                    onClick={() => move(i, i + 1)}
                    disabled={i === kpis.length - 1}
                  />
                </div>

                <div className="nv-kpilist__text">
                  <Toggle
                    checked={kpi.isEnabled}
                    onCheckedChange={(next) =>
                      change(kpis.map((k) => (k.kpi === kpi.kpi ? { ...k, isEnabled: next } : k)))
                    }
                    label={t(`mapSettings.kpi.${kpi.kpi}`)}
                  />
                  <p>{t(`mapSettings.kpiHint.${kpi.kpi}`)}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </>
  );
}
